import { useEffect, type RefObject } from "react";

/**
 * Tracks the pointer over `area` and writes the lamp position to `--lx` / `--ly` on `target`,
 * smoothed with a lerp. Touch screens get a slow autonomous drift instead.
 */
export function useLamp(area: RefObject<HTMLElement>, target: RefObject<HTMLElement>) {
  useEffect(() => {
    const host = area.current;
    const el = target.current;
    if (!host || !el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const touch = window.matchMedia("(hover: none)").matches;
    let tx = 0, ty = 0, sx = 0, sy = 0, seeded = false, raf = 0, drift = 0;

    const loop = () => {
      sx += (tx - sx) * 0.1;
      sy += (ty - sy) * 0.1;
      el.style.setProperty("--lx", `${sx.toFixed(1)}px`);
      el.style.setProperty("--ly", `${sy.toFixed(1)}px`);
      raf = Math.abs(tx - sx) > 0.2 || Math.abs(ty - sy) > 0.2 ? requestAnimationFrame(loop) : 0;
    };
    const aim = (x: number, y: number) => {
      tx = x;
      ty = y;
      if (!seeded) {
        sx = x;
        sy = y;
        seeded = true;
      }
      el.classList.add("is-lit");
      if (!raf) raf = requestAnimationFrame(loop);
    };

    if (reduce) return;

    if (touch) {
      const t0 = performance.now();
      const step = (now: number) => {
        const r = el.getBoundingClientRect();
        const t = (now - t0) / 1000;
        aim(r.width * (0.55 + 0.28 * Math.sin(t * 0.35)), r.height * (0.45 + 0.22 * Math.sin(t * 0.6)));
        drift = requestAnimationFrame(step);
      };
      drift = requestAnimationFrame(step);
      return () => {
        cancelAnimationFrame(drift);
        cancelAnimationFrame(raf);
      };
    }

    const move = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      aim(e.clientX - r.left, e.clientY - r.top);
    };
    const leave = () => el.classList.remove("is-lit");
    host.addEventListener("pointermove", move);
    host.addEventListener("pointerleave", leave);
    return () => {
      host.removeEventListener("pointermove", move);
      host.removeEventListener("pointerleave", leave);
      cancelAnimationFrame(raf);
    };
  }, [area, target]);
}
